import { useEffect, useState, useCallback } from "react";
import { useAssessmentSession } from "../contexts/assessmentUserContext";
import { getApiUrls } from "../lib/apiUrl";
import {
  createAssessmentBenchmarkRequest,
  listAssessmentBenchmarksRequest,
  type BenchmarkSummary,
} from "../services/assessmentContentService";

type UseAvailableAssessmentBenchmarksResult = {
  benchmarks: BenchmarkSummary[];
  benchmarkError: string | null;
  isSavingBenchmark: boolean;
  createBenchmark: (input: {
    title: string;
    benchmark_type: "boy" | "moy" | "eoy";
    skill: "reading" | "listening" | "speaking" | "writing";
    start_date: string;
    end_date: string;
    district_ids: string[];
    school_ids?: string[];
    grade_ids?: string[];
  }) => Promise<BenchmarkSummary>;
  reloadBenchmarks: () => Promise<void>;
};

export function useAvailableAssessmentBenchmarks(): UseAvailableAssessmentBenchmarksResult {
  const { token } = useAssessmentSession();
  const { assessmentsApiUrl: apiUrl } = getApiUrls();
  const [benchmarks, setBenchmarks] = useState<BenchmarkSummary[]>([]);
  const [benchmarkError, setBenchmarksError] = useState<string | null>(null);
  const [isSavingBenchmark, setIsSavingBenchmark] = useState<boolean>(false);

  const loadBenchmarks = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setBenchmarksError(null);
        const nextBenchmarks = await listAssessmentBenchmarksRequest({
          apiUrl,
          token,
          signal,
        });
        setBenchmarks(nextBenchmarks);
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setBenchmarksError(
          error instanceof Error
            ? error.message
            : "Unable to load assessment benchmarks.",
        );
      }
    },
    [apiUrl, token],
  );

  useEffect(() => {
    const controller = new AbortController();

    loadBenchmarks(controller.signal).then();

    return () => {
      controller.abort();
    };
  }, [apiUrl, token, loadBenchmarks]);

  const createBenchmark = async ({
    title,
    benchmark_type,
    skill,
    start_date,
    end_date,
    district_ids,
    school_ids,
    grade_ids,
  }: {
    title: string;
    benchmark_type: "boy" | "moy" | "eoy";
    skill: "reading" | "listening" | "speaking" | "writing";
    start_date: string;
    end_date: string;
    district_ids: string[];
    school_ids?: string[];
    grade_ids?: string[];
  }): Promise<BenchmarkSummary> => {
    setIsSavingBenchmark(true);
    try {
      return await createAssessmentBenchmarkRequest({
        apiUrl,
        token,
        title,
        benchmark_type,
        skill,
        start_date,
        end_date,
        district_ids,
        school_ids,
        grade_ids,
      });
    } finally {
      setIsSavingBenchmark(false);
    }
  };

  return {
    benchmarks,
    benchmarkError,
    isSavingBenchmark,
    createBenchmark,
    reloadBenchmarks: () => loadBenchmarks(),
  };
}
